/**
 * Admin dashboard — the customer-service home. Three sections:
 *   1. A single overall-health banner (green/amber/red) so staff see system status at a glance.
 *   2. Business metrics tiles (orgs, active/trialing/past-due/expiring/comped/canceled licenses, users).
 *   3. Per-monitor health rows with a plain-English summary (no raw JSON), and the most recent orgs.
 * All copy + counts come from the backend's getAdminDashboard; raw detail is available on demand.
 */
import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { adminGetDashboard, AdminApiError } from '../../src/api/adminClient';
import { Screen, Text, Card, Badge } from '../../src/components/ui';
import { licenseBadge } from '../../src/components/ui/statusMap';
import { AdminHeader } from '../../src/components/admin/AdminHeader';
import { monitorBadge } from '../../src/components/admin/adminBadge';
import { theme } from '../../src/theme';
import type { DashboardResponse } from '@beam/api-types';

function formatAt(at: string | null): string {
  if (!at) return 'never';
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? at : d.toLocaleString();
}

function formatDate(at: string): string {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? at : d.toLocaleDateString();
}

const OVERALL: Record<DashboardResponse['overall'], { bg: string; fg: string; title: string }> = {
  ok: { bg: theme.status.paid.bg, fg: theme.status.paid.fg, title: 'All systems operational' },
  warn: { bg: theme.status.inProgress.bg, fg: theme.status.inProgress.fg, title: 'Some checks need attention' },
  critical: { bg: theme.status.overdue.bg, fg: theme.status.overdue.fg, title: 'Action required' },
  unknown: { bg: theme.status.draft.bg, fg: theme.status.draft.fg, title: 'Status pending' },
};

function StatTile({ label, value, tone }: { label: string; value: number; tone?: 'default' | 'warn' | 'danger' }) {
  const color = tone === 'danger' ? theme.status.overdue.fg : tone === 'warn' ? theme.status.inProgress.fg : theme.color.text.primary;
  return (
    <View style={styles.tile}>
      <Text variant="amount" style={{ color }}>
        {value}
      </Text>
      <Text variant="label" color="secondary">
        {label}
      </Text>
    </View>
  );
}

export default function AdminDashboardScreen() {
  const router = useRouter();
  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: adminGetDashboard,
  });

  return (
    <Screen scroll>
      <AdminHeader active="dashboard" />

      {isLoading ? (
        <Card>
          <Text color="secondary">Loading system status…</Text>
        </Card>
      ) : error ? (
        <Card>
          <Text color="danger">{error instanceof AdminApiError || error instanceof Error ? error.message : 'Failed to load dashboard.'}</Text>
        </Card>
      ) : !data ? (
        <Card>
          <Text color="secondary">No data.</Text>
        </Card>
      ) : (
        <>
          {/* 1 — overall health banner */}
          <View style={[styles.banner, { backgroundColor: OVERALL[data.overall].bg }]}>
            <Text variant="subhead" style={{ color: OVERALL[data.overall].fg }}>
              {OVERALL[data.overall].title}
            </Text>
            <Text variant="small" style={{ color: OVERALL[data.overall].fg }}>
              Updated {formatAt(data.generatedAt)}
            </Text>
          </View>

          {/* 2 — business metrics */}
          <Text variant="label" color="secondary" style={styles.sectionLabel}>
            Accounts
          </Text>
          <Card>
            <View style={styles.tileGrid}>
              <StatTile label="Organizations" value={data.stats.totalOrgs} />
              <StatTile label="Active" value={data.stats.activeLicenses} />
              <StatTile label="Trialing" value={data.stats.trialing} />
              <StatTile label="Comped" value={data.stats.comped} />
              <StatTile label="Past due" value={data.stats.pastDue} tone={data.stats.pastDue > 0 ? 'danger' : 'default'} />
              <StatTile label="Expiring ≤7d" value={data.stats.expiringSoon} tone={data.stats.expiringSoon > 0 ? 'warn' : 'default'} />
              <StatTile label="Canceled" value={data.stats.canceled} />
              <StatTile label="Users" value={data.stats.totalUsers} />
            </View>
          </Card>

          {/* 3a — system health (humanized) */}
          <Text variant="label" color="secondary" style={styles.sectionLabel}>
            System health
          </Text>
          {data.monitors.map((m) => {
            const spec = monitorBadge(m.status);
            return (
              <Card key={m.monitor}>
                <View style={styles.cardHead}>
                  <Text variant="bodyStrong">{m.title}</Text>
                  <Badge status={spec.key} label={spec.label} />
                </View>
                <Text color="secondary">{m.summary}</Text>
                <Text variant="small" color="secondary" style={styles.lastChecked}>
                  Last checked: {formatAt(m.at)}
                </Text>
              </Card>
            );
          })}

          {/* 3b — recent organizations */}
          <View style={styles.recentHead}>
            <Text variant="label" color="secondary">
              Recent organizations
            </Text>
            <Text variant="small" color="link" onPress={() => router.push('/admin/orgs')} accessibilityRole="button">
              View all
            </Text>
          </View>
          {data.recentOrgs.length === 0 ? (
            <Card>
              <Text color="secondary">No organizations yet.</Text>
            </Card>
          ) : (
            data.recentOrgs.map((o) => {
              const badge = o.status ? licenseBadge(o.status) : null;
              return (
                <Pressable
                  key={o.organizationId}
                  accessibilityRole="button"
                  onPress={() => router.push(`/admin/orgs/${o.organizationId}`)}
                  style={({ pressed }) => [styles.orgRow, pressed && styles.orgRowPressed]}
                >
                  <View style={styles.orgText}>
                    <Text variant="bodyStrong" numberOfLines={1}>
                      {o.legalName}
                    </Text>
                    <Text variant="small" color="secondary" numberOfLines={1}>
                      {o.ownerEmail ?? 'no owner'} · joined {formatDate(o.createdAt)}
                    </Text>
                  </View>
                  <View style={styles.orgBadges}>
                    {o.isComped ? <Badge status="sent" label="Comped" /> : null}
                    {badge ? <Badge status={badge.key} label={badge.label} /> : null}
                  </View>
                </Pressable>
              );
            })
          )}
        </>
      )}

      <Text variant="small" color="link" onPress={() => void refetch()} accessibilityRole="button" style={styles.refresh}>
        {isRefetching ? 'Refreshing…' : 'Refresh'}
      </Text>
    </Screen>
  );
}

const styles = StyleSheet.create({
  banner: { borderRadius: theme.radius.md, padding: theme.spacing.lg, gap: theme.spacing.xs },
  sectionLabel: { marginTop: theme.spacing.lg, marginBottom: theme.spacing.sm },
  tileGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  tile: { width: '25%', minWidth: 80, paddingVertical: theme.spacing.sm, gap: theme.spacing.xs },
  cardHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: theme.spacing.xs },
  lastChecked: { marginTop: theme.spacing.sm },
  recentHead: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginTop: theme.spacing.lg, marginBottom: theme.spacing.sm },
  orgRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.md,
    backgroundColor: theme.color.bg.surface,
    borderRadius: theme.radius.md,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  orgRowPressed: { backgroundColor: theme.color.bg.subtle },
  orgText: { flex: 1, gap: 2 },
  orgBadges: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.xs, flexWrap: 'wrap', justifyContent: 'flex-end' },
  refresh: { paddingVertical: theme.spacing.sm, marginTop: theme.spacing.md },
});
